(function () {
  'use strict';

  var path = window.location.pathname || '/';
  var isCollectionPage = /\/collections\//i.test(path);
  var isSearchPage = /(^|\/)search\/?$/i.test(path) || !!document.querySelector('main-search');
  if (!isCollectionPage && !isSearchPage) return;

  var PREFETCH_DISTANCE = 2000;
  var REQUEST_TIMEOUT = 12000;
  var RETRY_DELAYS = [700, 1500, 2800, 5000, 8000];

  var state = {
    grid: null,
    pagination: null,
    controls: null,
    sentinel: null,
    status: null,
    button: null,
    nextUrl: '',
    loading: false,
    seen: new Set(),
    intersectionObserver: null,
    mutationObserver: null,
    controller: null,
    retryTimer: null,
    retryCount: 0,
    retryUrl: '',
    scrollTimer: null
  };

  if (!document.querySelector('[data-carturio-infinite-scroll-style]')) {
    var style = document.createElement('style');
    style.setAttribute('data-carturio-infinite-scroll-style', '');
    style.textContent =
      '.pagination-wrapper.carturio-infinite-scroll-pagination-hidden{display:none!important;}' +
      '.carturio-infinite-scroll-controls{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;margin:2rem auto 1.5rem;padding:0 1rem;text-align:center;}' +
      '.carturio-infinite-scroll-sentinel{display:block;width:1px;height:1px;pointer-events:none;}' +
      '.carturio-infinite-scroll-status{margin:0;color:rgba(var(--color-foreground,18,18,18),.68);font-size:1.35rem;line-height:1.5;}' +
      '.carturio-infinite-scroll-button{min-width:15rem;max-width:100%;}' +
      '.carturio-infinite-scroll-button[hidden]{display:none!important;}' +
      '.carturio-infinite-scroll-button:disabled{opacity:.65;cursor:wait;}' +
      '.carturio-infinite-scroll-item{animation:carturio-product-appear .22s ease-out both;}' +
      '@keyframes carturio-product-appear{from{opacity:.65;transform:translateY(7px)}to{opacity:1;transform:translateY(0)}}' +
      '@media(prefers-reduced-motion:reduce){.carturio-infinite-scroll-item{animation:none!important}}';
    document.head.appendChild(style);
  }

  function findProductGrid(doc) {
    if (!doc || !doc.querySelector) return null;
    var collectionGrid = doc.querySelector('#ProductGridContainer ul#product-grid.grid.product-grid');
    if (collectionGrid) return collectionGrid;
    var searchGrid = doc.querySelector('#ProductGridContainer #product-grid ul.grid.product-grid');
    if (searchGrid) return searchGrid;
    var genericGrid = doc.querySelector('#ProductGridContainer ul#product-grid');
    return genericGrid && genericGrid.classList.contains('product-grid') ? genericGrid : null;
  }

  function findPagination(doc) {
    return doc.querySelector('#ProductGridContainer .pagination-wrapper') ||
      doc.querySelector('.pagination-wrapper');
  }

  function getNextUrl(doc, baseUrl) {
    var pagination = findPagination(doc);
    if (!pagination) return '';

    // In this theme the forward-page arrow has the class pagination__item--prev.
    var link = pagination.querySelector('a.pagination__item--prev[href]') ||
      pagination.querySelector('a[rel="next"][href]');

    if (!link) {
      var links = Array.prototype.slice.call(pagination.querySelectorAll('a[href]'));
      link = links.find(function (candidate) {
        var label = (candidate.getAttribute('aria-label') || '').toLowerCase();
        return label.indexOf('next') !== -1 || label.indexOf('more') !== -1;
      });
    }
    if (!link) return '';

    try {
      var next = new URL(link.getAttribute('href'), baseUrl || window.location.href);
      if (next.origin !== window.location.origin) return '';
      if (baseUrl && next.href === new URL(baseUrl, window.location.href).href) return '';
      return next.href;
    } catch (_) {
      return '';
    }
  }

  function cardKey(card) {
    if (!card || !card.querySelectorAll) return '';
    var anchors = Array.prototype.slice.call(card.querySelectorAll('a[href]'));
    var productAnchor = anchors.find(function (a) {
      return /\/products\/[^/?#]+/i.test(a.getAttribute('href') || '');
    });
    var anchor = productAnchor || anchors[0];
    if (!anchor) return '';

    try {
      var url = new URL(anchor.getAttribute('href'), window.location.origin);
      if (url.origin !== window.location.origin) return '';
      var match = url.pathname.match(/\/products\/([^/]+)/i);
      if (match) return 'product:' + decodeURIComponent(match[1]).toLowerCase();
      return 'url:' + url.pathname.replace(/\/+$/, '') + url.search;
    } catch (_) {
      return '';
    }
  }

  function setStatus(message) {
    if (state.status) state.status.textContent = message;
  }

  function clearRetryTimer() {
    if (state.retryTimer) window.clearTimeout(state.retryTimer);
    state.retryTimer = null;
  }

  function removeCurrentUi() {
    if (state.intersectionObserver) state.intersectionObserver.disconnect();
    state.intersectionObserver = null;
    clearRetryTimer();

    if (state.controller) state.controller.abort();
    state.controller = null;
    state.loading = false;

    if (state.pagination) state.pagination.classList.remove('carturio-infinite-scroll-pagination-hidden');
    if (state.controls && state.controls.isConnected) state.controls.remove();

    state.grid = null;
    state.pagination = null;
    state.controls = null;
    state.sentinel = null;
    state.status = null;
    state.button = null;
    state.nextUrl = '';
    state.seen = new Set();
    state.retryCount = 0;
    state.retryUrl = '';
  }

  function setFinishedMessage() {
    setStatus('You’ve reached the end of this list.');
    if (state.button) {
      state.button.hidden = true;
      state.button.disabled = true;
    }
    clearRetryTimer();
    if (state.intersectionObserver) state.intersectionObserver.disconnect();
  }

  function scheduleRetry(url) {
    if (!url || state.nextUrl !== url || !state.grid || !state.grid.isConnected) return;
    if (state.retryUrl !== url) {
      state.retryUrl = url;
      state.retryCount = 0;
    }

    var index = Math.min(state.retryCount, RETRY_DELAYS.length - 1);
    var delay = RETRY_DELAYS[index];
    state.retryCount += 1;
    setStatus('Connection hiccup — retrying automatically…');

    // Keep a manual backup after repeated failures, but automatic retries continue.
    if (state.button && state.retryCount >= 2) {
      state.button.hidden = false;
      state.button.textContent = 'Retry now';
      state.button.disabled = false;
    }

    clearRetryTimer();
    state.retryTimer = window.setTimeout(function () {
      state.retryTimer = null;
      if (state.nextUrl !== url || state.loading || !state.grid || !state.grid.isConnected) return;
      if (document.visibilityState === 'hidden') {
        scheduleRetry(url);
        return;
      }
      loadNextPage();
    }, delay);
  }

  function prepareProductCard(card, indexInBatch) {
    // Dynamically inserted Shopify scroll-reveal cards may not be registered
    // with the theme animation observer. Remove those classes to prevent blanks.
    card.classList.remove('scroll-trigger', 'animate--slide-in');
    card.classList.add('carturio-infinite-scroll-item');
    card.removeAttribute('data-cascade');
    card.style.removeProperty('--animation-order');

    var images = Array.prototype.slice.call(card.querySelectorAll('img'));
    images.forEach(function (image) {
      image.decoding = 'async';

      // Prioritise the first row (or two on mobile) to avoid blank product cards.
      if (indexInBatch < 4) {
        image.loading = 'eager';
        if (indexInBatch < 2) image.fetchPriority = 'high';
      } else {
        image.loading = 'lazy';
        if ('fetchPriority' in image) image.fetchPriority = 'auto';
      }

      if (!image.alt) image.alt = '';
    });
  }

  function maybeLoadIfNearEnd() {
    if (!state.sentinel || !state.nextUrl || state.loading || !state.sentinel.isConnected) return;
    var rect = state.sentinel.getBoundingClientRect();
    if (rect.top <= window.innerHeight + PREFETCH_DISTANCE && rect.bottom >= -100) {
      loadNextPage();
    }
  }

  async function loadNextPage(options) {
    options = options || {};
    if (state.loading || !state.nextUrl || !state.grid || !state.grid.isConnected) return;
    if (options.force) {
      clearRetryTimer();
      state.retryCount = 0;
      state.retryUrl = '';
    }

    var gridAtRequest = state.grid;
    var urlAtRequest = state.nextUrl;
    var controller = new AbortController();
    var timedOut = false;
    var timeoutId = window.setTimeout(function () {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT);

    state.controller = controller;
    state.loading = true;

    setStatus('Loading more products…');
    if (state.button && !state.button.hidden) {
      state.button.disabled = true;
      state.button.setAttribute('aria-busy', 'true');
    }

    try {
      var response = await fetch(urlAtRequest, {
        method: 'GET',
        credentials: 'same-origin',
        signal: controller.signal,
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error('The next product page returned HTTP ' + response.status + '.');

      var html = await response.text();
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var remoteGrid = findProductGrid(parsed);
      if (!remoteGrid) throw new Error('The next product grid was not found.');
      if (state.grid !== gridAtRequest || !gridAtRequest.isConnected) return;

      var cards = Array.prototype.slice.call(remoteGrid.children).filter(function (child) {
        return child.matches && child.matches('li.grid__item');
      });
      var fragment = document.createDocumentFragment();
      var added = 0;

      cards.forEach(function (sourceCard) {
        var key = cardKey(sourceCard);
        if (key && state.seen.has(key)) return;
        if (key) state.seen.add(key);
        var cloned = document.importNode(sourceCard, true);
        prepareProductCard(cloned, added);
        fragment.appendChild(cloned);
        added += 1;
      });

      gridAtRequest.appendChild(fragment);
      state.nextUrl = getNextUrl(parsed, response.url || urlAtRequest);
      state.retryCount = 0;
      state.retryUrl = '';
      clearRetryTimer();

      if (state.nextUrl) {
        setStatus('Keep scrolling to discover more.');
        if (state.button) {
          state.button.textContent = 'Load more products';
          state.button.hidden = true;
          state.button.disabled = false;
          state.button.removeAttribute('aria-busy');
        }

        // If the user is already close to the end, chain the next fetch now
        // instead of waiting for a second observer transition.
        window.requestAnimationFrame(maybeLoadIfNearEnd);
      } else {
        setFinishedMessage();
      }
    } catch (error) {
      if (error && error.name === 'AbortError' && !timedOut) return;
      if (state.grid === gridAtRequest && gridAtRequest.isConnected) {
        setStatus(timedOut
          ? 'That took longer than expected — reconnecting automatically…'
          : 'A brief connection issue — reconnecting automatically…');
        scheduleRetry(urlAtRequest);
      }
    } finally {
      window.clearTimeout(timeoutId);
      if (state.controller === controller) {
        state.loading = false;
        state.controller = null;
        if (state.button && !state.button.hidden) {
          state.button.disabled = false;
          state.button.removeAttribute('aria-busy');
        }
      }
    }
  }

  function bindGrid(grid) {
    if (!grid || grid === state.grid) return;
    removeCurrentUi();

    state.grid = grid;
    Array.prototype.slice.call(grid.children).forEach(function (card) {
      var key = cardKey(card);
      if (key) state.seen.add(key);
    });

    var pagination = findPagination(document);
    var nextUrl = getNextUrl(document, window.location.href);
    if (!pagination || !nextUrl) return;

    state.pagination = pagination;
    state.nextUrl = nextUrl;
    pagination.classList.add('carturio-infinite-scroll-pagination-hidden');

    var controls = document.createElement('div');
    controls.className = 'carturio-infinite-scroll-controls';
    controls.innerHTML =
      '<span class="carturio-infinite-scroll-sentinel" aria-hidden="true"></span>' +
      '<p class="carturio-infinite-scroll-status" role="status" aria-live="polite">More products will appear as you scroll.</p>' +
      '<button type="button" class="button button--secondary carturio-infinite-scroll-button" hidden>Load more products</button>';

    state.controls = controls;
    state.sentinel = controls.querySelector('.carturio-infinite-scroll-sentinel');
    state.status = controls.querySelector('.carturio-infinite-scroll-status');
    state.button = controls.querySelector('.carturio-infinite-scroll-button');

    pagination.insertAdjacentElement('afterend', controls);
    state.button.addEventListener('click', function () {
      loadNextPage({ force: true });
    });

    if ('IntersectionObserver' in window) {
      state.intersectionObserver = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) loadNextPage();
      }, { rootMargin: PREFETCH_DISTANCE + 'px 0px', threshold: 0 });
      state.intersectionObserver.observe(state.sentinel);
    }

    // Fill short result pages without forcing the shopper to scroll to a tiny sentinel.
    window.requestAnimationFrame(maybeLoadIfNearEnd);
  }

  function scheduleScrollCheck() {
    if (state.scrollTimer) return;
    state.scrollTimer = window.setTimeout(function () {
      state.scrollTimer = null;
      maybeLoadIfNearEnd();
    }, 100);
  }

  function start() {
    var grid = findProductGrid(document);
    if (grid) bindGrid(grid);

    // Fallback for browsers where IntersectionObserver is missing or interrupted.
    window.addEventListener('scroll', scheduleScrollCheck, { passive: true });
    window.addEventListener('resize', scheduleScrollCheck, { passive: true });
    window.addEventListener('online', function () {
      if (state.nextUrl && !state.loading) loadNextPage();
    });

    // Shopify's faceted filters can replace the results section dynamically.
    state.mutationObserver = new MutationObserver(function () {
      var currentGrid = findProductGrid(document);
      if (currentGrid && currentGrid !== state.grid) {
        bindGrid(currentGrid);
      } else if (!currentGrid && state.grid && !state.grid.isConnected) {
        removeCurrentUi();
      }
    });
    state.mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', function () {
    var grid = findProductGrid(document);
    if (grid && grid !== state.grid) bindGrid(grid);
    scheduleScrollCheck();
  });
})();